import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { FormLabel } from "@/components/ui/form-label";
import { HStack } from "@/components/ui/h-stack";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import {
  getAgreementTerms,
  getLoadFields,
  createAgreementTerm,
  updateAgreementTerm,
  type AgreementTerm,
  type AgreementTermParam,
  type AgreementTermTexts,
} from "@/lib/api/agreement-terms";
import { fromIntlLanguage, type AppLanguage } from "@/lib/i18n/config";
import { CatalogPageTemplate } from "@/components/admin/catalog-page-template";
import { ParamAutocompleteTextarea } from "@/components/admin/catalogs/param-autocomplete-textarea";
import {
  DetectedParamsPanel,
  type ParamDraft,
} from "@/components/admin/catalogs/detected-params-panel";

type AgreementTermRow = AgreementTerm & { is_deleted?: boolean };

function toTextsPayload(fd: Record<string, unknown>): AgreementTermTexts {
  return {
    en: fd.textEn as string,
    ru: fd.textRu as string,
    tr: fd.textTr as string,
    uz: fd.textUz as string,
    uz_cyr: fd.textUz as string,
  };
}

function getLocalizedTermText(
  text: AgreementTermTexts | undefined,
  language: AppLanguage,
): string {
  if (!text) return "";
  switch (language) {
    case "RU":
      return text.ru || text.en;
    case "TR":
      return text.tr || text.en;
    case "UZ_LAT":
      return text.uz || text.en;
    case "UZ_CYR":
      return text.uz_cyr || text.en;
    default:
      return text.en;
  }
}

/** Return param keys found in allTexts but missing from `text`. */
function getMissingParams(text: string, allTexts: string[]): string[] {
  const allKeys = new Set<string>();
  for (const t of allTexts) {
    for (const m of t.matchAll(/\{(\w+)}/g)) allKeys.add(m[1]);
  }
  const present = new Set<string>();
  for (const m of text.matchAll(/\{(\w+)}/g)) present.add(m[1]);
  return [...allKeys].filter((k) => !present.has(k));
}

function MissingParamsWarning({
  text,
  allTexts,
  label,
}: {
  text: string;
  allTexts: string[];
  label: string;
}) {
  const missing = getMissingParams(text, allTexts);
  if (missing.length === 0) return null;
  return (
    <Text color="danger" className="mt-0.5 !text-xs">
      {label}: {missing.map((k) => `{${k}}`).join(", ")}
    </Text>
  );
}

function existingParamsToParamDrafts(
  params: AgreementTermParam[],
): ParamDraft[] {
  return params.map((p) => ({
    id: p.id,
    key: p.key,
    source: p.source,
    field_path: p.field_path,
    labels: p.label ?? { en: "", ru: "", tr: "", uz: "", uz_cyr: "" },
    sort_order: p.sort_order,
  }));
}

function AdminAgreementTermsPage() {
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t } = useTranslation("admin.catalogs.agreementTerms");

  // Extra query for load fields (needed by the form)
  const { data: loadFieldsData } = useQuery({
    queryKey: ["admin", "catalogs", "agreement-terms", "load-fields"],
    queryFn: () => getLoadFields(),
    staleTime: 60_000,
  });
  const loadFields = loadFieldsData ?? [];

  // Param drafts state lives outside the template since it's not part of the
  // generic Record<string, unknown> form data
  const [paramDrafts, setParamDrafts] = useState<ParamDraft[]>([]);

  return (
    <CatalogPageTemplate<AgreementTermRow>
      title={t("title")}
      tableId="catalog-agreement-terms"
      queryKey={["admin", "catalogs", "agreement-terms"]}
      fetchFn={getAgreementTerms}
      columns={[
        {
          key: "order",
          header: t("columns.order"),
          defaultWidth: 60,
          minWidth: 40,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.sort_order}
            </Text>
          ),
        },
        {
          key: "text",
          header: t("columns.text"),
          defaultWidth: 500,
          minWidth: 200,
          render: (item, isDeleted) => (
            <Text
              weight="medium"
              truncate
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {getLocalizedTermText(item.text, language)}
            </Text>
          ),
        },
      ]}
      searchFilter={(item, q) =>
        getLocalizedTermText(item.text, language).toLowerCase().includes(q)
      }
      searchPlaceholder={t("searchPlaceholder")}
      emptyMessage={t("noItems")}
      addLabel={t("add")}
      editTitle={t("modalEditTitle")}
      addTitle={t("modalAddTitle")}
      failedToLoadMessage={t("failedToLoad")}
      failedToSaveMessage={t("failedToSave")}
      deleteEndpoint="/agreement-terms"
      editLabel={t("edit")}
      onCreateOpen={() => setParamDrafts([])}
      onEditOpen={(item) =>
        setParamDrafts(existingParamsToParamDrafts(item.params ?? []))
      }
      itemToFormData={(item) => ({
        textEn: item.text?.en ?? "",
        textRu: item.text?.ru ?? "",
        textTr: item.text?.tr ?? "",
        textUz: item.text?.uz ?? "",
        sortOrder: String(item.sort_order ?? 0),
        defaultSelected: item.default_selected ?? true,
        required: item.required ?? false,
      })}
      emptyFormData={() => ({
        textEn: "",
        textRu: "",
        textTr: "",
        textUz: "",
        sortOrder: "0",
        defaultSelected: true,
        required: false,
      })}
      formDataToPayload={(fd) => {
        const paramsPayload = paramDrafts.map((p, i) => ({
          id: p.id,
          key: p.key,
          source: p.source,
          field_path: p.field_path,
          label: p.labels.en || p.key,
          labels: p.labels,
          sort_order: i,
        }));
        return {
          text: fd.textEn as string,
          texts: toTextsPayload(fd),
          sort_order: parseInt(fd.sortOrder as string, 10) || 0,
          default_selected: fd.defaultSelected as boolean,
          required: fd.required as boolean,
          params: paramsPayload,
        };
      }}
      isFormValid={(fd) => Boolean((fd.textEn as string).trim())}
      createFn={(payload) =>
        createAgreementTerm(
          payload as Parameters<typeof createAgreementTerm>[0],
        )
      }
      updateFn={(id, payload) =>
        updateAgreementTerm(
          id,
          payload as Parameters<typeof updateAgreementTerm>[1],
        )
      }
      renderForm={(formData, setFormData, submitting) => {
        const allTextsArray = [
          formData.textEn as string,
          formData.textRu as string,
          formData.textTr as string,
          formData.textUz as string,
        ];
        const textsForPanel = {
          en: formData.textEn as string,
          ru: formData.textRu as string,
          tr: formData.textTr as string,
          uz: formData.textUz as string,
        };

        return (
          <>
            <HStack gap="xl">
              <VStack gap="xs">
                <FormLabel>{t("fields.sortOrder")}</FormLabel>
                <InputWrapper
                  type="number"
                  value={formData.sortOrder as string}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sortOrder: e.target.value,
                    }))
                  }
                  disabled={submitting}
                  className="w-24"
                  data-testid="admin-agreement-term-sort-order"
                />
              </VStack>
              <FormLabel className="flex cursor-pointer items-center gap-2 pt-6">
                <Checkbox
                  checked={formData.defaultSelected as boolean}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      defaultSelected: checked,
                    }))
                  }
                  disabled={submitting}
                  data-testid="admin-agreement-term-default-selected"
                />
                <Text>{t("fields.defaultSelected")}</Text>
              </FormLabel>
              <FormLabel className="flex cursor-pointer items-center gap-2 pt-6">
                <Checkbox
                  checked={formData.required as boolean}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      required: checked,
                    }))
                  }
                  disabled={submitting}
                  className="h-4 w-4 rounded border-[color:var(--outline)] text-[color:var(--error)] focus:ring-[color:var(--error)]"
                  data-testid="admin-agreement-term-required"
                />
                <Text>{t("fields.required")}</Text>
              </FormLabel>
            </HStack>

            <HStack align="stretch" gap="xl">
              {/* Left column -- text fields */}
              <VStack className="min-w-0 flex-1">
                <VStack gap="xs">
                  <FormLabel>{t("fields.textEn")}</FormLabel>
                  <ParamAutocompleteTextarea
                    value={formData.textEn as string}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, textEn: val }))
                    }
                    loadFields={loadFields}
                    language={language}
                    disabled={submitting}
                    rows={2}
                    data-testid="admin-agreement-term-text-en"
                  />
                  <MissingParamsWarning
                    text={formData.textEn as string}
                    allTexts={allTextsArray}
                    label={t("params.missingParams")}
                  />
                </VStack>

                <VStack gap="xs">
                  <FormLabel>{t("fields.textRu")}</FormLabel>
                  <ParamAutocompleteTextarea
                    value={formData.textRu as string}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, textRu: val }))
                    }
                    loadFields={loadFields}
                    language={language}
                    disabled={submitting}
                    rows={2}
                    data-testid="admin-agreement-term-text-ru"
                  />
                  <MissingParamsWarning
                    text={formData.textRu as string}
                    allTexts={allTextsArray}
                    label={t("params.missingParams")}
                  />
                </VStack>

                <VStack gap="xs">
                  <FormLabel>{t("fields.textTr")}</FormLabel>
                  <ParamAutocompleteTextarea
                    value={formData.textTr as string}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, textTr: val }))
                    }
                    loadFields={loadFields}
                    language={language}
                    disabled={submitting}
                    rows={2}
                    data-testid="admin-agreement-term-text-tr"
                  />
                  <MissingParamsWarning
                    text={formData.textTr as string}
                    allTexts={allTextsArray}
                    label={t("params.missingParams")}
                  />
                </VStack>

                <VStack gap="xs">
                  <FormLabel>{t("fields.textUz")}</FormLabel>
                  <ParamAutocompleteTextarea
                    value={formData.textUz as string}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, textUz: val }))
                    }
                    loadFields={loadFields}
                    language={language}
                    disabled={submitting}
                    rows={2}
                    data-testid="admin-agreement-term-text-uz"
                  />
                  <MissingParamsWarning
                    text={formData.textUz as string}
                    allTexts={allTextsArray}
                    label={t("params.missingParams")}
                  />
                </VStack>
              </VStack>

              {/* Right column -- params panel */}
              <VStack className="w-72 shrink-0">
                <DetectedParamsPanel
                  texts={textsForPanel}
                  params={paramDrafts}
                  onParamsChange={setParamDrafts}
                  loadFields={loadFields}
                  language={language}
                  disabled={submitting}
                />
              </VStack>
            </HStack>
          </>
        );
      }}
      testIdPrefix="admin-catalog-agreement-terms"
    />
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/agreement-terms")({
  component: AdminAgreementTermsPage,
});
